
import React, { useState, useEffect, useRef } from 'react';
import { usePost } from '../../api/reactQueryCustomHooks';
import { apiRoute } from '../../constants/enum';
import jsBarcode from 'jsbarcode';
import { useSelector } from "react-redux";
import html2canvas from 'html2canvas';
import { MainContainer } from '../../components';

const BarcodeGenerator = () => {
    const [inputValue, setInputValue] = useState('');
    const [lotNo, setLotNo] = useState('');
    const [pktNo, setPktNo] = useState('');
    const [diamondData, setDiamondData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [diamondList, setDiamondList] = useState([]);
    const { compCode } = useSelector((state) => state.company);
    const cardRef = useRef(null);

    const { mutate: fetchStockData } = usePost({
        queryKey: "stockData",
        url: apiRoute.packetTransferEntry.getStockData,
    });

    useEffect(() => {
        if (diamondData) {
            generateBarcode();
        }
    }, [diamondData]);

    const handleInputChange = (e) => {
        setInputValue(e.target.value);
    };
console.log(diamondData)
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault();
            processInput();
        }
    };

    const processInput = () => {
        const parts = inputValue.split('#');
        if (parts.length === 2) {
            const [lot, pkt] = parts;
            setLotNo(lot);
            setPktNo(pkt);
            setIsLoading(true);
            setError(null);

            fetchStockData({
                data: {
                    compcode: compCode,
                    lotno: lot,
                    pktno: pkt,
                    tradeof: "P"
                }
            }, {
                onSuccess: (response) => {
                    if (response?.[0]) {
                        const item = response[0];
                        const newDiamondData = {
                            shapeCode: item.ShapeCode || '',
                            weight: item.BWeight || '',
                            color: item.ColorCode || '',
                            clarity: item.PurityCode || '',
                            ta: item.TBL_DPTH || '',
                            td: item.TBL_DPTG || '',
                            cut: item.CUT || '',
                            polish: item.POLISH || '',
                            symmetry: item.SYMTRY || '',
                            fluorescence: item.FLRN || '',
                            measurements: item.MSRMNT || '',
                            ratio: item.DIA && item.STN_LNTH ? (item.DIA / item.STN_LNTH).toFixed(2) : '',
                            reportNo: item.ReportNo || '',
                            lotNo: lot,
                            pktNo: pkt
                        };
                        setDiamondData(newDiamondData);
                        setDiamondList(prev => [...prev, newDiamondData]);
                    } else {
                        setError('No data found for the provided LotNo and PktNo');
                    }
                },
                onError: (err) => {
                    console.error('API error:', err);
                    setError('Failed to fetch stock data');
                },
                onSettled: () => {
                    setIsLoading(false);
                    setInputValue('');
                }
            });
        } else {
            setError('Please enter in the format LotNo#PktNo (e.g., PA#6)');
        }
    };

    const generateBarcode = () => {
        try {
            const canvas = document.createElement('canvas');

            jsBarcode(canvas, `${diamondData.lotNo}#${diamondData.pktNo}`, {
                format: "CODE128",
                lineColor: "#000",
                width: 2,
                height: 40,
                displayValue: false,
                margin: 0
            });

            const barcodeContainer = document.getElementById('barcode-container');
            if (barcodeContainer) {
                barcodeContainer.innerHTML = '';
                barcodeContainer.appendChild(canvas);
            }
        } catch (err) {
            console.error('Barcode generation error:', err);
            setError('Failed to generate barcode');
        }
    };

    const downloadDiamondCard = async (diamond) => {
        if (!cardRef.current) return;

        try {
            const canvas = await html2canvas(cardRef.current, {
                scale: 2,
                logging: false,
                useCORS: true,
                allowTaint: true
            });

            const link = document.createElement('a');
            link.download = `diamond_${diamond.lotNo}_${diamond.pktNo}.jpg`;
            link.href = canvas.toDataURL('image/jpeg', 1.0);
            link.click();
        } catch (err) {
            console.error('Error generating image:', err);
            setError('Failed to generate image');
        }
    };

    const printAllDiamondCards = async () => {
        if (diamondList.length === 0) return;

        // Create a new window for printing
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            alert('Please allow pop-ups for this site to print multiple cards');
            return;
        }

        // Basic HTML structure
        let htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
            <title>Diamond Cards Print</title>
            <style>
                @page {
                    size: 80mm 50mm;
                    margin: 0;
                }
                body {
                    margin: 0;
                    padding: 0;
                    font-family: Arial, sans-serif;
                    width: 80mm;
                    height: 50mm;
                }
                .diamond-card {
                    width: 80mm;
                    height: 50mm;
                    padding: 1mm;
                    display: flex;
                    page-break-after: always;
                    box-sizing: border-box;
                    border-bottom: 1px dashed #ccc;
                }
                .left-side {
                    width: 60%;
                    padding-right: 2mm;
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                }
                .right-side {
                    width: 40%;
                    padding-left: 2mm;
                    display: flex;
                    flex-direction: column;
                    font-size: 12px;
                }
                .diamond-title {
                    font-weight: bold;
                    font-size: 15px;
                    margin-bottom: 1mm;
                }
                .diamond-info {
                    font-weight: bold;
                    font-size: 15px;
                    margin-bottom: 1mm;
                }
                .barcode-container {
                    width: 100%;
                    height: 20mm; 
                    display: flex;
                    align-items: flex-end;
                    justify-content: center;
                }
                .barcode {
                    width: 100%;
                    height: 100%;
                }
            </style>
        </head>
        <body>
    `;

        // Add each diamond card to the HTML
        diamondList.forEach((diamond) => {
            htmlContent += `
            <div class="diamond-card">
                <div class="left-side">
                    <div>
                        <div class="diamond-title">${diamond.shapeCode} ${diamond.weight}</div>
                        <div class="diamond-info">COLOR : ${diamond.color}</div>
                        <div class="diamond-info">CLEARITY : ${diamond.clarity}</div>
                        <div class="diamond-info">TA : ${diamond.ta}</div>
                        <div class="diamond-info">TD : ${diamond.td}</div>
                    </div>
                    <div class="barcode-container">
                        <svg id="barcode-${diamond.lotNo}-${diamond.pktNo}" class="barcode"></svg>
                    </div>
                </div>
                <div class="right-side">
                    <div class="diamond-info">${diamond.lotNo}-${diamond.pktNo}</div>
                    <div class="diamond-info">CUT : ${diamond.cut}</div>
                    <div class="diamond-info">POL : ${diamond.polish}</div>
                    <div class="diamond-info">SYM : ${diamond.symmetry}</div>
                    <div class="diamond-info">FL : ${diamond.fluorescence}</div>
                    <div class="diamond-info">DIA : ${diamond.measurements}</div>
                    <div class="diamond-info">RATIO : ${diamond.ratio} %</div>
                    <div class="diamond-info">Report No : ${diamond.reportNo}</div>
                </div>
            </div>
        `;
        });

        htmlContent += `
            <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
            <script>
                document.addEventListener('DOMContentLoaded', function() {
                    // Generate all barcodes
                    ${diamondList.map(diamond => `
                        try {
                            JsBarcode(
                                document.getElementById('barcode-${diamond.lotNo}-${diamond.pktNo}'), 
                                '${diamond.lotNo}#${diamond.pktNo}', 
                                {
                                    format: "CODE128",
                                    lineColor: "#000",
                                    width: 2, /* Increased width for better readability */
                                    height: 50, /* Increased height significantly */
                                    displayValue: false,
                                    margin: 5 /* Added some margin */
                                }
                            );
                        } catch(e) {
                            console.error('Barcode error for ${diamond.lotNo}-${diamond.pktNo}:', e);
                        }
                    `).join('')}
                    
                    // Wait a bit to ensure all barcodes are rendered
                    setTimeout(function() {
                        window.print();
                        window.close();
                    }, 500);
                });
            </script>
        </body>
        </html>
    `;

        // Write the content to the new window
        printWindow.document.open();
        printWindow.document.write(htmlContent);
        printWindow.document.close();
    };

    const removeDiamondCard = (index) => {
        setDiamondList(prev => prev.filter((_, i) => i !== index));
    };

    return (
        <MainContainer
            pageName={`Barcode Print`}
            info={[]}
            breadcrumbs={[" Diamond Params", "Barcode Print"]}
        >
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 ">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-8">
                        <h1 className="text-2xl font-bold text-gray-800 mb-1">Diamond Card Generator</h1>
                        <p className="text-gray-600">Generate and manage diamond identification cards with barcodes</p>
                    </div>

                    <div className="flex flex-col lg:flex-row gap-6">
                        {/* Left Side - Diamond List */}
                        <div className="w-full lg:w-1/2 bg-white rounded-xl shadow-md overflow-hidden">
                            <div className="p-6">
                                <div className="flex justify-between items-center mb-6">
                                    <h2 className="text-xl font-semibold text-gray-800">Generated Cards</h2>
                                    {diamondList.length > 0 && (
                                        <button
                                            onClick={printAllDiamondCards}
                                            className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-1 px-2 rounded-lg transition duration-200 ease-in-out shadow-sm flex items-center"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                                            </svg>
                                            Print All ({diamondList.length})
                                        </button>
                                    )}
                                </div>

                                {diamondList.length === 0 ? (
                                    <div className="text-center py-8">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                                        </svg>
                                        <p className="mt-2 text-gray-500">No diamond cards generated yet</p>
                                        <p className="text-sm text-gray-400">Enter a LotNo#PktNo to get started</p>
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto max-h-[600px]">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50 sticky top-0">
                                                <tr>
                                                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Shape
                                                    </th>
                                                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Weight
                                                    </th>
                                                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Color
                                                    </th>
                                                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Clarity
                                                    </th>
                                                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Lot-Pkt
                                                    </th>
                                                    <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Actions
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {diamondList.map((diamond, index) => (
                                                    <tr key={index} className="hover:bg-gray-50">
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                                            {diamond.shapeCode}
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                                            {diamond.weight}
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                                            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                                                                {diamond.color}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                                            <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                                                                {diamond.clarity}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                                            {diamond.lotNo}-{diamond.pktNo}
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                                                            <div className="flex justify-end space-x-2">
                                                                <button
                                                                    onClick={() => {
                                                                        setDiamondData(diamond);
                                                                        setTimeout(generateBarcode, 100);
                                                                    }}
                                                                    className="text-indigo-600 hover:text-indigo-800 p-1 rounded-full hover:bg-indigo-50"
                                                                    title="Preview"
                                                                >
                                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                                    </svg>
                                                                </button>
                                                                <button
                                                                    onClick={() => downloadDiamondCard(diamond)}
                                                                    className="text-green-600 hover:text-green-800 p-1 rounded-full hover:bg-green-50"
                                                                    title="Download"
                                                                >
                                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                                    </svg>
                                                                </button>
                                                                <button
                                                                    onClick={() => removeDiamondCard(index)}
                                                                    className="text-red-600 hover:text-red-800 p-1 rounded-full hover:bg-red-50"
                                                                    title="Remove"
                                                                >
                                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                    </svg>
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Right Side - Generator */}
                        <div className="w-full lg:w-1/2 bg-white rounded-xl shadow-md overflow-hidden">
                            <div className="p-6">
                                <h2 className="text-xl font-semibold text-green-800 mb-6">Generate New Card</h2>

                                <div className="mb-6">
                                    <label htmlFor="diamond-input" className="block text-sm font-medium text-gray-700 mb-1">
                                        Enter LotNo#PktNo
                                    </label>
                                    <div className="relative flex items-center">
                                        <input
                                            id="diamond-input"
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition duration-200"
                                            type="text"
                                            value={inputValue}
                                            onChange={handleInputChange}
                                            onKeyDown={handleKeyDown}
                                            placeholder="PA#6"
                                            aria-label="Lot number and packet number"
                                        />
                                        <button
                                            onClick={processInput}
                                            className="ml-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-1 px-2 rounded-lg transition duration-200 ease-in-out shadow-sm flex items-center"
                                            type="button"
                                        >
                                            {isLoading ? (
                                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                            ) : (
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                                </svg>
                                            )}
                                            Generate
                                        </button>
                                    </div>
                                    <p className="mt-1 text-xs text-gray-500">Example: PA#6, ABC123#45, XYZ#789</p>
                                </div>

                                {error && (
                                    <div className="mb-6 p-3 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-r">
                                        <div className="flex items-center">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                            </svg>
                                            <p>{error}</p>
                                        </div>
                                    </div>
                                )}

                                {diamondData && (
                                    <div className="mt-6">
                                        <div className="flex justify-between items-center mb-4">
                                            <h3 className="text-lg font-medium text-pink-600">Card Preview</h3>
                                            <button
                                                onClick={() => downloadDiamondCard(diamondData)}
                                                className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-1 px-2 rounded-lg transition duration-200 ease-in-out shadow-sm flex items-center"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                </svg>
                                                Download
                                            </button>
                                        </div>

                                        <div
                                            ref={cardRef}
                                            className="border border-gray-200 p-4 flex bg-white rounded-lg shadow-sm"
                                            style={{
                                                width: '100%',
                                                maxWidth: '110mm',
                                                height: '70mm',
                                                boxSizing: 'border-box'
                                            }}
                                        >
                                            <div className="w-1/2 pr-4 flex flex-col justify-between border-r border-gray-200">
                                                <div>
                                                    <div className="font-bold text-xl mb-2 text-gray-800">{diamondData.shapeCode} {diamondData.weight}</div>
                                                    <div className="text-sm mb-2 text-gray-700">COLOR : <span className="font-medium">{diamondData.color}</span></div>
                                                    <div className="text-sm mb-2 text-gray-700">CLEARITY : <span className="font-medium">{diamondData.clarity}</span></div>
                                                    <div className="text-sm mb-2 text-gray-700">TA : <span className="font-medium">{diamondData.ta}</span></div>
                                                    <div className="text-sm text-gray-700">TD : <span className="font-medium">{diamondData.td}</span></div>
                                                </div>
                                                <div id="barcode-container" className="w-full flex items-end" style={{ height: '25mm' }}></div>
                                            </div>
                                            <div className="w-1/2 pl-4 text-sm flex flex-col">
                                                <div className="mb-2 text-gray-800 font-medium">{diamondData.lotNo}-{diamondData.pktNo}</div>
                                                <div className="mb-2 text-gray-700">CUT : <span className="font-medium">{diamondData.cut}</span></div>
                                                <div className="mb-2 text-gray-700">POL : <span className="font-medium">{diamondData.polish}</span></div>
                                                <div className="mb-2 text-gray-700">SYM : <span className="font-medium">{diamondData.symmetry}</span></div>
                                                <div className="mb-2 text-gray-700">FL : <span className="font-medium">{diamondData.fluorescence}</span></div>
                                                <div className="mb-2 text-gray-700">DIA : <span className="font-medium">{diamondData.measurements}</span></div>
                                                <div className="mb-2 text-gray-700">RATIO : <span className="font-medium">{diamondData.ratio}%</span></div>
                                                <div className="text-gray-700">Report No : <span className="font-medium">{diamondData.reportNo}</span></div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {!diamondData && (
                                    <div className="text-center py-12">
                                        <div className="inline-block p-4 bg-indigo-50 rounded-full mb-4">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                                            </svg>
                                        </div>
                                        <h3 className="text-lg font-medium text-gray-700 mb-2">No Card Preview</h3>
                                        <p className="text-gray-500">Enter a valid LotNo#PktNo to generate a diamond card</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </MainContainer>
    );
};

export default BarcodeGenerator;
